import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { toast } from "sonner";
import { AnimatePresence, motion } from "framer-motion";
import { X, CalendarDays, Clock, Loader2, CheckCircle2 } from "lucide-react";
import { useLang } from "../LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SLOTS = ["09:00 – 10:00", "10:30 – 11:30", "14:00 – 15:00", "16:00 – 17:00", "18:00 – 19:00"];
const EMPTY = { service: "", date: "", slot: "", name: "", email: "", phone: "" };

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const label = (d, opts) => new Intl.DateTimeFormat("fr-FR", opts).format(d);
const longDate = (s) => label(new Date(`${s}T12:00:00`), { weekday: "long", day: "numeric", month: "long" });

export const BookingTrigger = ({ className = "", children, testid = "booking-open-button" }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" data-testid={testid} className={className} onClick={() => setOpen(true)}>
        {children}
      </button>
      <BookingDialog open={open} onClose={() => setOpen(false)} />
    </>
  );
};

const BookingDialog = ({ open, onClose }) => {
  const { lang, s } = useLang();
  const b = s.booking;
  const locale = lang === "fr" ? "fr-FR" : "en-GB";
  const [form, setForm] = useState(EMPTY);
  const [status, setStatus] = useState("idle");

  const days = useMemo(() => {
    const out = [];
    const now = new Date();
    for (let i = 1; i <= 14; i++) {
      const d = new Date(now);
      d.setDate(now.getDate() + i);
      out.push(d);
    }
    return out;
  }, []);

  useEffect(() => {
    if (open) {
      setStatus("idle");
      setForm(EMPTY);
    }
  }, [open]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const dayShort = (d) => new Intl.DateTimeFormat(locale, { weekday: "short" }).format(d);
  const dayNum = (d) => new Intl.DateTimeFormat(locale, { day: "numeric", month: "short" }).format(d);
  const longDate = (v) => new Intl.DateTimeFormat(locale, { weekday: "long", day: "numeric", month: "long" }).format(new Date(`${v}T12:00:00`));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.date || !form.slot) {
      toast.error(b.onError);
      return;
    }
    if (status === "sending") return;
    setStatus("sending");
    try {
      const { data } = await axios.post(`${API}/bookings`, { ...form, lang });
      setStatus("success");
      toast.success(data?.email_sent ? s.toasts.bookingOk : s.toasts.bookingOkNoEmail);
    } catch (err) {
      setStatus("idle");
      toast.error(err.response?.data?.detail?.message || s.toasts.bookingErr);
    }
  };

  const close = () => {
    onClose();
  };

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="booking-dialog"
          className="fixed inset-0 z-[70] flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div data-testid="booking-backdrop" className="absolute inset-0 bg-obsidian/85 backdrop-blur-sm" onClick={close} />
          <motion.div
            className="glass relative z-10 max-h-[88vh] w-full max-w-lg overflow-y-auto rounded-3xl p-7 sm:p-9"
            initial={{ y: 44, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 24, opacity: 0 }}
            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
          >
            <button
              data-testid="booking-close-button"
              onClick={close}
              aria-label={b.ariaClose}
              className="absolute right-5 top-5 rounded-full border border-white/10 bg-white/5 p-2 text-white/70 transition-colors hover:text-white"
            >
              <X className="h-4 w-4" />
            </button>

            {status === "success" ? (
              <div data-testid="booking-success" className="flex flex-col items-center py-8 text-center">
                <CheckCircle2 className="h-14 w-14 text-mint" />
                <h3 className="mt-6 font-serif text-3xl text-white">{b.successTitle}</h3>
                <p className="mt-4 max-w-sm text-sm leading-relaxed text-white/65">
                  {b.successText(form.service, longDate(form.date), form.slot)}
                </p>
                <button data-testid="booking-success-close" onClick={close} className="btn-ghost mt-8">
                  {b.close}
                </button>
              </div>
            ) : (
              <form data-testid="booking-form" onSubmit={submit}>
                <p className="eyebrow">{b.eyebrow}</p>
                <h3 className="mt-3 flex items-center gap-3 font-serif text-3xl font-light text-white">
                  <CalendarDays className="h-6 w-6 text-gold" /> {b.title}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-white/55">{b.intro}</p>

                <label className="mt-6 block">
                  <span className="mb-2 block font-mono text-[11px] uppercase tracking-[0.22em] text-white/50">{b.serviceLabel}</span>
                  <select
                    required
                    data-testid="booking-service-select"
                    className="field appearance-none"
                    value={form.service}
                    onChange={(e) => set("service")(e.target.value)}
                  >
                    <option value="" className="bg-night">{b.servicePh}</option>
                    {s.offers.map((o) => (
                      <option key={o.id} value={`${o.title} (${o.tagline}) — ${o.price}`} className="bg-night">
                        {o.title} — {o.tagline} — {o.price}
                      </option>
                    ))}
                    <option value={b.unknown} className="bg-night">{b.unknown}</option>
                  </select>
                </label>

                <span className="mb-2 mt-5 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-white/50">
                  <CalendarDays className="h-3.5 w-3.5 text-gold/70" /> {b.dateLabel} *
                </span>
                <div data-testid="booking-date-strip" className="flex gap-2 overflow-x-auto pb-2">
                  {days.map((d) => {
                    const v = iso(d);
                    const active = form.date === v;
                    return (
                      <button
                        type="button"
                        key={v}
                        data-testid={`booking-date-${v}`}
                        onClick={() => set("date")(v)}
                        className={`shrink-0 rounded-xl border px-3.5 py-2.5 text-center transition-all duration-200 ${
                          active
                            ? "border-gold/70 bg-gold/15 text-gold-light"
                            : "border-white/10 bg-white/[0.04] text-white/70 hover:border-gold/40"
                        }`}
                      >
                        <span className="block text-[10px] uppercase tracking-wider">{dayShort(d)}</span>
                        <span className="block text-sm font-semibold">{dayNum(d)}</span>
                      </button>
                    );
                  })}
                </div>

                <span className="mb-2 mt-4 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-white/50">
                  <Clock className="h-3.5 w-3.5 text-gold/70" /> {b.slotLabel} *
                </span>
                <div data-testid="booking-slot-grid" className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                  {SLOTS.map((slot) => (
                    <button
                      type="button"
                      key={slot}
                      data-testid={`booking-slot-${slot.slice(0, 5).replace(":", "")}`}
                      onClick={() => set("slot")(slot)}
                      className={`rounded-xl border px-3 py-2.5 text-xs font-medium transition-all duration-200 ${
                        form.slot === slot
                          ? "border-mint/70 bg-mint/15 text-mint"
                          : "border-white/10 bg-white/[0.04] text-white/70 hover:border-mint/40"
                      }`}
                    >
                      {slot}
                    </button>
                  ))}
                </div>

                <div className="mt-5 grid gap-4 sm:grid-cols-2">
                  <label className="block">
                    <span className="mb-2 block font-mono text-[11px] uppercase tracking-[0.22em] text-white/50">{b.nameLabel}</span>
                    <input required data-testid="booking-name-input" className="field" value={form.name} onChange={(e) => set("name")(e.target.value)} placeholder={b.namePh} />
                  </label>
                  <label className="block">
                    <span className="mb-2 block font-mono text-[11px] uppercase tracking-[0.22em] text-white/50">{b.emailLabel}</span>
                    <input required type="email" data-testid="booking-email-input" className="field" value={form.email} onChange={(e) => set("email")(e.target.value)} placeholder={b.emailPh} />
                  </label>
                  <label className="block sm:col-span-2">
                    <span className="mb-2 block font-mono text-[11px] uppercase tracking-[0.22em] text-white/50">{b.phoneLabel}</span>
                    <input type="tel" data-testid="booking-phone-input" className="field" value={form.phone} onChange={(e) => set("phone")(e.target.value)} placeholder={b.phonePh} />
                  </label>
                </div>

                <button type="submit" data-testid="booking-submit-button" disabled={status === "sending"} className="btn-gold mt-7 w-full disabled:cursor-wait disabled:opacity-60">
                  {status === "sending" ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarDays className="h-4 w-4" />}
                  {status === "sending" ? b.sending : b.submit}
                </button>
                <p className="mt-4 text-center text-[11px] text-white/40">{b.note}</p>
              </form>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};
